import React from 'react';
import { useSelector } from 'react-redux';
import { Text } from '@rsos/components/capstone/base';
import type { RootState } from '../../../app/rootReducer';
import { getProductToken } from '../../../utils/getProductToken';
import { RADECard } from './RADECard';
import {
  BodySection,
  CardContainer,
  HeaderSection,
} from './RADECardNext.styles';
import { selectRadeNextView, type RadeNextView } from './radeSelectors';
import { useFetchRadeDataLink } from './useFetchRadeDataLink';

const selectProductToken = (state: RootState): string =>
  (getProductToken(state.sinatra) as { productToken?: string }).productToken ??
  '';

interface HeaderCopy {
  title: string;
  source: string;
}

/**
 * Title + source copy for the card header. Kept tiny and local —
 * if the set of lookup kinds grows we can move this to a registry.
 */
const headerCopyFor = (view: RadeNextView): HeaderCopy => {
  if (view.kind === 'maf') {
    return { title: 'MedicAlert Profile', source: 'MedicAlert' };
  }
  return { title: 'Additional Data', source: 'Phone-number lookup' };
};

/**
 * GlobalMapSearch-scoped RAD-e container.
 *
 * Modern replacement for `RADECardContainer` for the GlobalMapSearch
 * experience. This container is intentionally fetch-free: the data
 * for the active lookup is pre-fetched by `useMedicAlertSearch` /
 * `useAdrSearch` (the hooks the dropdown calls) and stored under
 * `radeSlice.mafLookup.data` / `adrLookup.data`. We just pick the
 * channel `selectedLookup` points at and render it.
 *
 * That separation keeps the dropdown row click instant — no spinner
 * round-trip on open — and avoids the legacy `RADECard.tsx`
 * dependency on `state.search.searchTerm` (owned by
 * `ConnectedUniversalSearch`) entirely.
 *
 * Header chrome (title + source line) follows the same pattern as
 * other modern map cards (`PureWirelessCard`, the spatial layer
 * cards' `StyledMapCardHeader`) instead of the legacy empty
 * `<SearchHeader />` placeholder. The view-projection (which
 * channel, gating by access, etc.) is collapsed into a single
 * memoized selector (`selectRadeNextView`) so the body stays a pure
 * render of its result. The EllingtonCard data-link callback lives
 * in `useFetchRadeDataLink`.
 */
const RADECardNext: React.FC = () => {
  const view = useSelector(selectRadeNextView);
  const productToken = useSelector(selectProductToken);
  const onFetchDataLink = useFetchRadeDataLink();

  if (!view) return null;

  const { title, source } = headerCopyFor(view);

  return (
    <CardContainer>
      <HeaderSection>
        <Text
          color="primaryText"
          style={{ fontSize: '14px', fontWeight: 600, marginBottom: '2px' }}
        >
          {title}
        </Text>
        <Text color="secondaryText" size="xsmall">
          Source: {source}
        </Text>
      </HeaderSection>
      <BodySection>
        <RADECard
          data={view.data}
          loading={view.status === 'pending'}
          error={view.status === 'error'}
          accessToken={productToken}
          onFetchDataLink={onFetchDataLink}
        />
      </BodySection>
    </CardContainer>
  );
};

export default RADECardNext;
